import type {
  InvoicePartiesQueryParams,
  InvoicePartiesResponse,
} from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';
import { log } from '~/utils/logging';

import {
  zodInvoicePartiesQueryParamsSchema,
  zodInvoicePartiesResponseSchema,
} from './validations';

const EMPTY_RESPONSE: InvoicePartiesResponse = {
  data: [],
  hasNextPage: false,
  paginatedCount: 0,
  totalCount: null,
};

/**
 * Fetches the distinct companies that appear as parties (buyer or seller) on
 * the invoices accessible to the user, for the invoice list party filters.
 *
 * @param queryParams - The query parameters for fetching invoice parties.
 * @returns The paginated invoice party filter options.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/get_invoice_party_filter_options_invoice_filter_options_parties_get
 */
export const fetchInvoiceParties = async (
  queryParams: Partial<InvoicePartiesQueryParams>,
  options?: KyRequestOptions,
) => {
  const response = await vestigasApi
    .get(ENDPOINT.INVOICE.GET_FILTER_PARTIES(), {
      searchParams: queryParams,
      validate: {
        searchParams: zodInvoicePartiesQueryParamsSchema.transform((data) =>
          withoutObjectKeysWhereValueIs(data, [undefined]),
        ),
      },
      ...options,
    })
    .json<InvoicePartiesResponse | undefined>();

  // Validate the returned data and log any validation errors
  const validatedResponse = zodInvoicePartiesResponseSchema.safeParse(response);

  if (!validatedResponse.success) {
    log.error({
      message: 'Invoice parties API response has validation errors',
      error: {
        errors: validatedResponse.error.issues,
        errorMessage: validatedResponse.error.message,
        response,
      },
    });
  }

  return response ?? EMPTY_RESPONSE;
};
